// app/inventory/[category]/useCatalogParams.ts
"use client";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { ReservedSearchParams } from "../[[...category]]/reserved-params";

export type CatalogParams = {
    categorySlug: string; // from pathname
    q: string | null;
    page: number;
    cursor: string | null;
    attributeFilters: Record<string, string | string[]>; // supports multi-select
    tags: string[]; // active tag-filter keys (reserved, kept out of attributeFilters)
};

export function useCatalogParams(): CatalogParams {
    const pathname = usePathname(); // e.g. /inventory/forklifts-electric
    const sp = useSearchParams();   // ReadonlyURLSearchParams

    return useMemo(() => {
        const parts = pathname.split("/").filter(Boolean);
        const categorySlug = parts[1] ?? "";     // inventory/[category]

        const q = sp.get("q");
        const cursor = sp.get("cursor");
        const page = Number(sp.get("page") ?? 1) || 1;

        // Tags are multi-valued (?tags=a&tags=b); also tolerate a comma-joined form.
        const tags = Array.from(
            new Set(sp.getAll("tags").flatMap((v) => v.split(",")).map((s) => s.trim()).filter(Boolean))
        );

        // Attributes: everything that isn't reserved
        const attributeFilters: Record<string, string | string[]> = {};
        for (const [key, value] of sp.entries()) {
            if (ReservedSearchParams.has(key)) continue;

            // Handle multiple occurrences of the same parameter
            // e.g., ?brand=Toyota&brand=Nissan&brand=Honda -> brand: ["Toyota", "Nissan", "Honda"]
            const all = sp.getAll(key);
            attributeFilters[key] = all.length > 1 ? all : value;
        }

        return { categorySlug, q, page, cursor, attributeFilters, tags };
    }, [pathname, sp]);
}